import { useCallback, useMemo, useState } from "react";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import {
  Beaker,
  CheckCircle2,
  Loader2,
  ShieldCheck,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  getStripe,
  getStripeEnvironment,
  isPaymentsConfigured,
  isPaymentsTestMode,
} from "@/lib/stripe";
import { getSubscriptionByEmail } from "@/lib/subscription";

/**
 * In-app, end-to-end live payment verification.
 *
 * Why this exists: before launching to real users, we want a dependable way
 * to confirm that the full payment loop works on the *live* Stripe account
 * — not just sandbox. That loop is:
 *
 *   1. Frontend asks `create-checkout` for a live client_secret.
 *   2. User completes Stripe's embedded checkout with a real card.
 *   3. Stripe fires `customer.subscription.created` to `payments-webhook`.
 *   4. The webhook upserts a row into `public.subscriptions` keyed by email,
 *      with `plan`, `status`, `stripe_subscription_id`, and (for first-time
 *      customers) `trial_end`.
 *
 * We intentionally use the `founding_monthly` price so the test costs
 * nothing on the first run — first-time customers get a 14-day free trial
 * (`useTrial: true` in the edge function). The Subscription is still
 * created in Stripe with `status: trialing`, the webhook still fires, and
 * we still get a real DB row to inspect. After the test you can cancel
 * with one click from the same panel.
 *
 * Returning customers (already have `stripe_customer_id`) skip the trial
 * and would be charged £3 — we surface that explicitly before they start.
 */

type CheckName =
  | "checkout_created"
  | "webhook_received"
  | "plan_set"
  | "trial_or_active"
  | "stripe_ids_present";

type CheckStatus = "pending" | "running" | "pass" | "fail";

type CheckResult = {
  name: CheckName;
  label: string;
  status: CheckStatus;
  detail?: string;
};

const INITIAL_CHECKS: CheckResult[] = [
  {
    name: "checkout_created",
    label: "Live checkout session created",
    status: "pending",
  },
  {
    name: "webhook_received",
    label: "Webhook received & subscription row written",
    status: "pending",
  },
  {
    name: "plan_set",
    label: "Plan tier saved (founding / standard)",
    status: "pending",
  },
  {
    name: "trial_or_active",
    label: "Status is trialing or active",
    status: "pending",
  },
  {
    name: "stripe_ids_present",
    label: "Stripe customer + subscription IDs linked",
    status: "pending",
  },
];

type Snapshot = {
  status: string | null;
  stripe_subscription_id: string | null;
  trial_end: string | null;
  plan: string | null;
};

type Props = {
  email: string;
};

export const PaymentVerificationPanel = ({ email }: Props) => {
  const env = getStripeEnvironment();
  const isTest = isPaymentsTestMode();
  const configured = isPaymentsConfigured();

  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState<"idle" | "preparing" | "checkout" | "verifying" | "done">(
    "idle",
  );
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [checks, setChecks] = useState<CheckResult[]>(INITIAL_CHECKS);
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [summary, setSummary] = useState<string | null>(null);

  const updateCheck = (name: CheckName, patch: Partial<CheckResult>) => {
    setChecks((prev) => prev.map((c) => (c.name === name ? { ...c, ...patch } : c)));
  };

  const reset = () => {
    setPhase("idle");
    setClientSecret(null);
    setChecks(INITIAL_CHECKS);
    setSnapshot(null);
    setSummary(null);
  };

  const fetchClientSecret = useCallback(async (): Promise<string> => {
    // The embedded provider asks for the client secret on mount. We've
    // already fetched it in `start()`; just hand it back synchronously.
    if (clientSecret) return clientSecret;
    throw new Error("No client secret available");
  }, [clientSecret]);

  const checkoutOptions = useMemo(
    () => ({
      fetchClientSecret,
      onComplete: () => {
        // Stripe finished collecting payment / setting up the trial.
        // Now poll our DB to confirm the webhook wrote the row.
        setPhase("verifying");
        void verify();
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetchClientSecret],
  );

  const start = async () => {
    if (!configured) {
      toast.error("Payments are not configured in this build.");
      return;
    }
    setPhase("preparing");
    setChecks(INITIAL_CHECKS);
    setSummary(null);

    // Snapshot existing row so we can detect a *change* after webhook fires.
    try {
      const existing = await getSubscriptionByEmail(email);
      setSnapshot({
        status: existing?.status ?? null,
        stripe_subscription_id: null, // we don't expose this in the public type
        trial_end: existing?.trial_end ?? null,
        plan: existing?.plan ?? null,
      });
    } catch {
      setSnapshot(null);
    }

    updateCheck("checkout_created", { status: "running" });
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: {
          priceId: "founding_monthly",
          customerEmail: email,
          environment: env,
          returnUrl: `${window.location.origin}/account?verify=done`,
        },
      });
      if (error || !data?.clientSecret) {
        updateCheck("checkout_created", {
          status: "fail",
          detail: error?.message || data?.error || "No client secret returned",
        });
        setPhase("done");
        setSummary("Could not create a live checkout session — see check #1.");
        return;
      }
      updateCheck("checkout_created", {
        status: "pass",
        detail: data.foundingFallback
          ? "Founding cap hit — fell back to standard_monthly"
          : data.trialing
            ? "Trial mode (no charge)"
            : "Returning customer — card will be charged",
      });
      setClientSecret(data.clientSecret);
      setPhase("checkout");
    } catch (err) {
      console.error(err);
      updateCheck("checkout_created", {
        status: "fail",
        detail: err instanceof Error ? err.message : "Unknown error",
      });
      setPhase("done");
    }
  };

  const verify = async () => {
    updateCheck("webhook_received", { status: "running" });

    // Poll for up to 20s. The webhook usually lands within 1–2s.
    const deadline = Date.now() + 20_000;
    let row: Awaited<ReturnType<typeof getSubscriptionByEmail>> = null;
    while (Date.now() < deadline) {
      row = await getSubscriptionByEmail(email);
      // Treat as "received" when status is active/trialing AND the row is
      // either brand new or has flipped from a prior state (snapshot diff).
      const flipped =
        row &&
        ((row.status === "trialing" || row.status === "active") &&
          (!snapshot ||
            snapshot.status !== row.status ||
            snapshot.trial_end !== row.trial_end));
      if (flipped) break;
      await new Promise((r) => setTimeout(r, 1000));
    }

    if (!row) {
      updateCheck("webhook_received", {
        status: "fail",
        detail: "No subscription row found after 20s",
      });
      ["plan_set", "trial_or_active", "stripe_ids_present"].forEach((n) =>
        updateCheck(n as CheckName, { status: "fail", detail: "Skipped — no row" }),
      );
      setPhase("done");
      setSummary(
        "Webhook never wrote the row. Check `payments-webhook` logs for `?env=live`.",
      );
      return;
    }

    updateCheck("webhook_received", {
      status: "pass",
      detail: `Row found (status: ${row.status})`,
    });

    // Plan tier
    if (row.plan === "founding" || row.plan === "standard") {
      updateCheck("plan_set", { status: "pass", detail: `plan = ${row.plan}` });
    } else {
      updateCheck("plan_set", {
        status: "fail",
        detail: `plan = ${row.plan ?? "null"} (expected founding/standard)`,
      });
    }

    // Status
    if (row.status === "trialing" || row.status === "active") {
      const trialBit =
        row.status === "trialing" && row.trial_end
          ? ` · trial_end ${new Date(row.trial_end).toLocaleDateString()}`
          : "";
      updateCheck("trial_or_active", {
        status: "pass",
        detail: `${row.status}${trialBit}`,
      });
    } else {
      updateCheck("trial_or_active", {
        status: "fail",
        detail: `status = ${row.status}`,
      });
    }

    // Stripe IDs — query directly so we can see stripe_subscription_id (not
    // exposed in SubscriptionStatus). RLS allows the signed-in user to
    // SELECT their own row.
    try {
      const { data: full } = await supabase
        .from("subscriptions")
        .select("stripe_customer_id, stripe_subscription_id")
        .eq("email", email.toLowerCase().trim())
        .maybeSingle();
      const hasCustomer = Boolean(full?.stripe_customer_id);
      const hasSub = Boolean(full?.stripe_subscription_id);
      if (hasCustomer && hasSub) {
        updateCheck("stripe_ids_present", {
          status: "pass",
          detail: "Both IDs linked",
        });
      } else {
        updateCheck("stripe_ids_present", {
          status: "fail",
          detail: `customer=${hasCustomer ? "yes" : "no"}, subscription=${hasSub ? "yes" : "no"}`,
        });
      }
    } catch (err) {
      updateCheck("stripe_ids_present", {
        status: "fail",
        detail: err instanceof Error ? err.message : "Lookup failed",
      });
    }

    setPhase("done");
    setSummary(
      "End-to-end test complete. If everything passed, live checkout is verified. " +
        "You can cancel the test subscription from the Plan section above.",
    );
  };

  const allPassed = checks.every((c) => c.status === "pass");
  const anyFailed = checks.some((c) => c.status === "fail");

  return (
    <>
      <section className="mb-6 rounded-2xl border border-border bg-card p-5 shadow-soft">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
            <Beaker className="h-4 w-4" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="font-display text-lg font-semibold">
              Verify live payments
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Run a real end-to-end test against{" "}
              <span className="font-medium text-foreground">
                {isTest ? "test mode" : "live mode"}
              </span>
              . Uses the £3 Founding Monthly plan with a 14-day free trial — first-time
              subscribers aren't charged. Returning customers will be charged £3.
            </p>
            <Button
              size="sm"
              className="mt-4 rounded-full"
              onClick={() => {
                reset();
                setOpen(true);
                void start();
              }}
              disabled={!configured}
            >
              <ShieldCheck className="mr-2 h-4 w-4" />
              Run payment test
            </Button>
          </div>
        </div>
      </section>

      <Sheet
        open={open}
        onOpenChange={(o) => {
          if (!o && phase === "checkout") {
            // Don't allow accidental dismissal mid-checkout — Stripe iframe
            // is mounted with a live client_secret that can't be reused.
            return;
          }
          setOpen(o);
          if (!o) reset();
        }}
      >
        <SheetContent
          side="right"
          className="w-full max-w-xl overflow-y-auto sm:max-w-xl"
        >
          <SheetHeader>
            <SheetTitle>Live payment verification</SheetTitle>
            <SheetDescription>
              Step through the embedded checkout with a real card. Once Stripe
              confirms, we'll check the database and webhook in real time.
            </SheetDescription>
          </SheetHeader>

          {/* Checks list */}
          <ol className="mt-6 space-y-2">
            {checks.map((c, i) => (
              <li
                key={c.name}
                className="flex items-start gap-3 rounded-xl border border-border bg-background/50 p-3"
              >
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center">
                  {c.status === "pass" ? (
                    <CheckCircle2 className="h-5 w-5 text-primary" />
                  ) : c.status === "fail" ? (
                    <XCircle className="h-5 w-5 text-destructive" />
                  ) : c.status === "running" ? (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  ) : (
                    <span className="text-xs text-muted-foreground">{i + 1}</span>
                  )}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground">{c.label}</p>
                  {c.detail && (
                    <p className="mt-0.5 text-xs text-muted-foreground">{c.detail}</p>
                  )}
                </div>
              </li>
            ))}
          </ol>

          {/* Summary */}
          {phase === "done" && summary && (
            <div
              className={
                allPassed
                  ? "mt-4 rounded-xl border border-primary/30 bg-primary/5 p-3 text-sm text-foreground"
                  : anyFailed
                    ? "mt-4 rounded-xl border border-destructive/30 bg-destructive/5 p-3 text-sm text-foreground"
                    : "mt-4 rounded-xl border border-border bg-muted/40 p-3 text-sm text-foreground"
              }
            >
              {summary}
            </div>
          )}

          {/* Embedded checkout */}
          {phase === "checkout" && clientSecret && (
            <div className="mt-6">
              <EmbeddedCheckoutProvider
                stripe={getStripe()}
                options={checkoutOptions}
              >
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            </div>
          )}

          {phase === "verifying" && (
            <div className="mt-6 flex items-center gap-3 rounded-xl border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Waiting for Stripe webhook…
            </div>
          )}

          {/* Footer actions */}
          {phase === "done" && (
            <div className="mt-6 flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                className="rounded-full"
                onClick={() => {
                  setOpen(false);
                  reset();
                }}
              >
                Close
              </Button>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
};
